import type { ChangeEvent } from "./types";

type MessageHandler = (event: ChangeEvent) => void;
type OpenHandler = () => void;

const MIN_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30_000;

export class RealtimeConnection {
  #socket: WebSocket | null = null;
  #messageHandlers = new Set<MessageHandler>();
  #openHandlers = new Set<OpenHandler>();
  #backoff = MIN_BACKOFF_MS;
  #reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  #closedByUser = false;

  connect(): void {
    this.#closedByUser = false;
    this.#open();
  }

  disconnect(): void {
    this.#closedByUser = true;
    if (this.#reconnectTimer) clearTimeout(this.#reconnectTimer);
    this.#socket?.close();
    this.#socket = null;
  }

  onMessage(handler: MessageHandler): () => void {
    this.#messageHandlers.add(handler);
    return () => this.#messageHandlers.delete(handler);
  }

  onOpen(handler: OpenHandler): () => void {
    this.#openHandlers.add(handler);
    return () => this.#openHandlers.delete(handler);
  }

  #open(): void {
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(`${protocol}//${location.host}/ws`);
    this.#socket = socket;

    socket.addEventListener("open", () => {
      this.#backoff = MIN_BACKOFF_MS;
      for (const handler of this.#openHandlers) handler();
    });

    socket.addEventListener("message", (event) => {
      try {
        const parsed = JSON.parse(event.data as string) as ChangeEvent;
        for (const handler of this.#messageHandlers) handler(parsed);
      } catch {
        // ignore malformed messages
      }
    });

    socket.addEventListener("close", () => {
      if (this.#closedByUser) return;
      this.#scheduleReconnect();
    });

    socket.addEventListener("error", () => {
      socket.close();
    });
  }

  #scheduleReconnect(): void {
    if (this.#reconnectTimer) return;
    this.#reconnectTimer = setTimeout(() => {
      this.#reconnectTimer = null;
      this.#backoff = Math.min(this.#backoff * 2, MAX_BACKOFF_MS);
      this.#open();
    }, this.#backoff);
  }
}
